import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../../../themes/Default/components/Form/components/Input";
import { Textarea } from "../../../../themes/Default/components/Form/components/Textarea";
import { Select } from "../../../../themes/Default/components/Form/components/Select";
import { studentsManagement } from "../../../../hooks/api/StudentsManagement";
import { useTranslation } from "react-i18next";
import { prepareSelect } from "../../../../services/core/Select";
import { noteTypesManagement } from "../../../../hooks/api/NoteTypesManagement/NoteTypesManagement";

const AddNote = () => {
    const { t } = useTranslation("common");
    const { studentId } = useParams();
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [formErrors, setFormErrors] = useState([]);
    const [noteTypes, setNoteTypes] = useState([]);
    const { useAddNoteMutation } = studentsManagement;
    const [addNote, { isLoading: isAddingNote }] = useAddNoteMutation();
    const { useNoteTypesListMutation } = noteTypesManagement;
    const [noteTypesList] = useNoteTypesListMutation();

    const tabs = [
        { title: t("details"), onClick: () => gotoDetailsTab(studentId) },
        // { title: t("students.tests"), onClick: () => gotoTestsTab(studentId) },
        { title: t("students.contract"), onClick: () => gotoContractsTab(studentId) },
        { title: t("calls.title"), onClick: () => gotoCallsTab(studentId) },
        { title: t("groups.title"), onClick: () => gotoGroupsTab(studentId) },
        { title: t("students.certificates"), onClick: () => gotoCertificatesTab(studentId) },
        { title: t("documents"), onClick: () => gotoDocumentsTab(studentId) },
        { title: t("notes"), isActive: true },
    ];

    const gotoCallsTab = (studentId) => {
        navigate("/students/" + studentId + "/calls");
    };

    const gotoContractsTab = (studentId) => {
        navigate("/students/" + studentId + "/contracts");
    };

    const gotoCertificatesTab = (studentId) => {
        navigate('/students/' + studentId + '/certificates');
    }

    const gotoDetailsTab = (studentId) => {
        navigate("/students/edit/" + studentId);
    };

    const gotoTestsTab = (studentId) => {
        navigate('/students/' + studentId + '/tests');
    }

    const gotoGroupsTab = (studentId) => {
        navigate('/students/' + studentId + '/groups');
    }

    const gotoDocumentsTab = (studentId) => {
        navigate("/students/" + studentId + "/documents");
    };

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        addNote({ body: values, studentId }).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
            } else {
                navigate(`/students/${studentId}/notes`);
            }
        });
    };

    useEffect(() => {
        noteTypesList().then((result) => {
            let options = [];
            result?.data?.records.forEach((note) => {
                options.push({
                    id: note.id,
                    name: note.name,
                });
            });
            setNoteTypes(prepareSelect(options));
        });
    }, [noteTypesList]);

    return (
        <DefaultLayout>
            <TopNavigation>
                <ButtonBack to={'/students/' + studentId + '/notes'} />
                <Button title={t("save")} isDisabled={isAddingNote} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t("students.title")} icon="fa-user-graduate" />
                    <Link title={t("notes")} />
                    <Link title={t("details")} />
                    <Link title={t("students.addNotes")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        type: "",
                        title: "",
                        description: "",
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Select
                                        name="type"
                                        label={t("students.typeName")}
                                        options={noteTypes}
                                        onChange={handleChange}
                                        selectedValue={values.type}
                                        error={formErrors?.type}
                                        required={true}
                                    />
                                </Field>
                                <Field>
                                    <Input 
                                        name="title" 
                                        label={t("students.noteTitle")} 
                                        value={values.title} 
                                        onChange={handleChange} 
                                        error={formErrors?.title} 
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea
                                        rows="7"
                                        name="description"
                                        label={t("students.description")}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={formErrors?.description}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default AddNote;
